/**
 * The herd's pure rules: event payload shapes, the facet reducers for animals, lots and places, and
 * "was this animal here on that date". Shared by the contract (facets), the server (validation,
 * inventory, reconciliation) and the web (sections). No DOM, no Worker APIs.
 */
import type { FacetEvent, Money, Quantity } from "../../../contract";
import {
  DEPARTURE_KEEPS_TITLE, DEPARTURE_STATUS,
  type ArrivalKind, type CullReason, type DeathCause, type DepartureKind, type IdAction, type IdScheme, type InterestKind, type PriceBasis,
} from "./vocab";

// ---------------------------------------------------------------- event payloads

export interface ArrivalData {
  kind: ArrivalKind; head?: number; class?: string; owner_id?: string; avg_weight?: Quantity; price?: Money; price_basis?: PriceBasis;
  origin?: string; lease_until?: string; reference?: string; notes?: string;
}
export interface DepartureData {
  kind: DepartureKind; head?: number; class?: string; reason?: string; avg_weight?: Quantity; price?: Money; price_basis?: PriceBasis;
  destination?: string; leaves_on?: string; lease_until?: string; reference?: string; notes?: string;
}
export interface DeathData { found_on?: string; cause?: DeathCause; basis?: string; found_at?: string; disposal?: string; head?: number; notes?: string }
export interface CullData { decision?: "cull" | "keep"; reason?: CullReason; target?: string; notes?: string }
export interface MissingData { last_seen_on?: string; last_seen_at?: string; notes?: string }
export interface FoundData { found_at?: string; notes?: string }
export interface IdentifierData { action: IdAction; scheme?: IdScheme; new_value?: string; old_value?: string; issuer?: string; ear?: string; reason?: string; notes?: string }
export interface WeaningData { method?: string; notes?: string }
export interface Holder { contact_id: string; share?: number }
export interface OwnershipData { interest?: InterestKind; holders?: Holder[]; our_share?: number; until?: string; basis?: string; agreement?: string; notes?: string }
export interface CountData { counted: number; expected?: number; class?: string; notes?: string }
export interface LotData { action?: "join" | "leave" | "move"; head?: number; already_counted?: boolean; notes?: string }

// ---------------------------------------------------------------- dates

type Datable = { type: string; occurred_at: string | null; recorded_at?: string; data: unknown };

/**
 * The date an event takes effect. A death whose date is unknown still happened by the day the
 * animal was found, so the found date stands in for ordering and counting.
 */
export function effectiveOn(e: Datable): string | null {
  const on = e.occurred_at ? e.occurred_at.slice(0, 10) : null;
  if (!on && e.type === "herd.death") return (e.data as DeathData | undefined)?.found_on ?? null;
  return on;
}

export function byEffective<T extends Datable>(a: T, b: T): number {
  const x = effectiveOn(a) ?? "9999";
  const y = effectiveOn(b) ?? "9999";
  return x === y ? (a.recorded_at ?? "").localeCompare(b.recorded_at ?? "") : x.localeCompare(y);
}

/** Head an event moves: the stated head for count-only lots, otherwise the animals named. */
export function headOf(e: { data: unknown; subjects: Array<{ role: string; kind: string }> }, role = "animal"): number {
  const stated = (e.data as { head?: number } | undefined)?.head;
  if (typeof stated === "number") return stated;
  return e.subjects.filter((s) => s.role === role && s.kind === "animal").length;
}

// ---------------------------------------------------------------- the animal facet

export type HerdStatus = "active" | "sold" | "dead" | "transferred" | "missing";

export interface Interest {
  holders: Holder[];
  /** The ranch's own share when stated. Undefined means not recorded, never zero by default. */
  our_share?: number;
  since: string | null;
  until?: string;
  basis?: string;
  via: "arrival" | "departure" | "ownership";
  event_id: string;
}

export interface LifePoint {
  on: string | null;
  change: "in" | "out" | "missing" | "found";
  kind: string;
  event_id: string;
}

export interface HerdFacet {
  status?: HerdStatus;
  arrival?: { on: string | null; kind: ArrivalKind; from_id?: string; event_id: string };
  departure?: { on: string | null; reason: DepartureKind; to_id?: string; destination?: string; leaves_on?: string; lease_until?: string; event_id: string };
  death?: { on: string | null; precision: string; found_on?: string; cause?: DeathCause; basis?: string; found_at?: string; event_id: string };
  cull?: { on: string | null; reason?: CullReason; target?: string; event_id: string };
  missing?: { since: string | null; last_seen_on?: string; last_seen_at?: string; event_id: string };
  found?: { on: string | null; at?: string; event_id: string };
  tag_lost?: { on: string | null; event_id: string };
  lease?: { direction: "in" | "out"; contact_id?: string; until?: string };
  weaned_on?: string;
  /** Title holders other than the ranch (shared key for other modules). */
  owner_ids?: string[];
  our_share?: number;
  interests?: Partial<Record<InterestKind, Interest>>;
  ownership_history?: Array<{ on: string | null; interest: InterestKind; holders: Holder[]; our_share?: number; via: Interest["via"]; event_id: string }>;
  /** Lot named by the latest herd lot event; undefined when no herd event placed it in a lot. */
  lot_id?: string | null;
  timeline: LifePoint[];
  // Flat keys for list filters and the agent's find_animals facets filter.
  on_cull_list?: boolean;
  needs_tag?: boolean;
  arrival_kind?: ArrivalKind;
  leased?: "in" | "out";
  weaned?: boolean;
}

const LIFECYCLE = new Set(["herd.arrival", "herd.departure", "herd.death", "herd.missing", "herd.found"]);

export function reduceHerd(events: FacetEvent[]): HerdFacet | null {
  if (!events.length) return null;
  const s: HerdFacet = { timeline: [] };
  const history: NonNullable<HerdFacet["ownership_history"]> = [];
  const setInterest = (kind: InterestKind, value: Interest) => {
    s.interests = { ...(s.interests ?? {}), [kind]: value };
    history.push({ on: value.since, interest: kind, holders: value.holders, our_share: value.our_share, via: value.via, event_id: value.event_id });
  };
  const contact = (e: FacetEvent, role: string) => e.subjects.find((x) => x.role === role && x.kind === "contact")?.id;
  const lotOf = (e: FacetEvent) => e.subjects.find((x) => x.role === "lot" && x.kind === "group")?.id;

  for (const e of [...events].sort(byEffective)) {
    const on = effectiveOn(e);
    switch (e.type) {
      case "herd.arrival": {
        if (!e.roles.includes("animal")) break;
        const d = e.data as ArrivalData;
        const from = contact(e, "from");
        s.status = "active";
        s.arrival = { on, kind: d.kind, ...(from ? { from_id: from } : {}), event_id: e.id };
        s.departure = undefined;
        s.missing = undefined;
        s.timeline.push({ on, change: "in", kind: d.kind, event_id: e.id });
        if (d.kind === "leased_in") {
          s.lease = { direction: "in", ...(from ? { contact_id: from } : {}), ...(d.lease_until ? { until: d.lease_until } : {}) };
          if (from) setInterest("title", { holders: [{ contact_id: from, share: 100 }], our_share: 0, since: on, via: "arrival", event_id: e.id });
        } else {
          if (s.lease?.direction === "out" && d.kind === "returned") s.lease = undefined;
          if (d.owner_id) setInterest("title", { holders: [{ contact_id: d.owner_id, share: 100 }], our_share: 0, since: on, via: "arrival", event_id: e.id });
          else if (d.kind === "purchased" || d.kind === "born_elsewhere") setInterest("title", { holders: [], our_share: 100, since: on, via: "arrival", event_id: e.id });
        }
        const lot = lotOf(e);
        if (lot) s.lot_id = lot;
        break;
      }
      case "herd.departure": {
        const d = e.data as DepartureData;
        const to = contact(e, "to");
        s.status = DEPARTURE_STATUS[d.kind] ?? "sold";
        s.departure = { on, reason: d.kind, ...(to ? { to_id: to } : {}), ...(d.destination ? { destination: d.destination } : {}),
          ...(d.leaves_on ? { leaves_on: d.leaves_on } : {}), ...(d.lease_until ? { lease_until: d.lease_until } : {}), event_id: e.id };
        s.missing = undefined;
        s.cull = undefined;
        s.timeline.push({ on, change: "out", kind: d.kind, event_id: e.id });
        if (d.kind === "leased_out") s.lease = { direction: "out", ...(to ? { contact_id: to } : {}), ...(d.lease_until ? { until: d.lease_until } : {}) };
        else if (s.lease?.direction === "in") s.lease = undefined;
        if (!DEPARTURE_KEEPS_TITLE[d.kind]) setInterest("title", { holders: to ? [{ contact_id: to, share: 100 }] : [], our_share: 0, since: on, via: "departure", event_id: e.id });
        break;
      }
      case "herd.death": {
        const d = e.data as DeathData;
        s.status = "dead";
        s.death = { on, precision: e.occurred_at ? e.occurred_precision : "unknown", ...(d.found_on ? { found_on: d.found_on } : {}), ...(d.cause ? { cause: d.cause } : {}),
          ...(d.basis ? { basis: d.basis } : {}), ...(d.found_at ? { found_at: d.found_at } : {}), event_id: e.id };
        s.missing = undefined;
        s.cull = undefined;
        s.timeline.push({ on, change: "out", kind: "death", event_id: e.id });
        break;
      }
      case "herd.cull": {
        const d = e.data as CullData;
        if (d.decision === "keep") s.cull = undefined;
        else s.cull = { on, ...(d.reason ? { reason: d.reason } : {}), ...(d.target ? { target: d.target } : {}), event_id: e.id };
        break;
      }
      case "herd.missing": {
        const d = e.data as MissingData;
        if (s.status === undefined || s.status === "active" || s.status === "missing") s.status = "missing";
        s.missing = { since: on, ...(d.last_seen_on ? { last_seen_on: d.last_seen_on } : {}), ...(d.last_seen_at ? { last_seen_at: d.last_seen_at } : {}), event_id: e.id };
        s.timeline.push({ on, change: "missing", kind: "missing", event_id: e.id });
        break;
      }
      case "herd.found": {
        const d = e.data as FoundData;
        if (s.status === "missing" || s.status === undefined) s.status = "active";
        s.missing = undefined;
        s.found = { on, ...(d.found_at ? { at: d.found_at } : {}), event_id: e.id };
        s.timeline.push({ on, change: "found", kind: "found", event_id: e.id });
        break;
      }
      case "herd.identifier": {
        const d = e.data as IdentifierData;
        if (d.action === "lost") s.tag_lost = { on, event_id: e.id };
        else if (d.action === "retag" && (d.scheme ?? "visual") === "visual") s.tag_lost = undefined;
        break;
      }
      case "herd.weaning": {
        if (on) s.weaned_on = on;
        break;
      }
      case "herd.ownership": {
        const d = e.data as OwnershipData;
        const kind = d.interest ?? "title";
        setInterest(kind, {
          holders: d.holders ?? [], ...(d.our_share !== undefined ? { our_share: d.our_share } : {}), since: on,
          ...(d.until ? { until: d.until } : {}), ...(d.basis ? { basis: d.basis } : {}), via: "ownership", event_id: e.id,
        });
        break;
      }
      case "herd.lot": {
        const d = e.data as LotData;
        const lot = lotOf(e);
        if (!lot) break;
        const action = d.action ?? "join";
        if (action === "join") {
          s.lot_id = lot;
          // Untagged lot cattle that just got tags were already counted with the lot; as individuals they start here.
          if (d.already_counted && !s.timeline.some((p) => p.change === "in")) s.timeline.push({ on, change: "in", kind: "tagged_in_lot", event_id: e.id });
        } else if (action === "move") {
          const to = e.subjects.find((x) => x.role === "to_lot" && x.kind === "group")?.id;
          if (to) s.lot_id = to;
        } else if (s.lot_id === lot) s.lot_id = null;
        break;
      }
    }
  }

  const title = s.interests?.title;
  if (title) {
    const ids = title.holders.map((h) => h.contact_id);
    if (ids.length) s.owner_ids = ids;
    if (title.our_share !== undefined) s.our_share = title.our_share;
  }
  if (history.length) s.ownership_history = history.slice(-20);
  const gone = s.status === "sold" || s.status === "dead" || s.status === "transferred";
  if (gone) { s.cull = undefined; s.tag_lost = undefined; }
  if (s.cull) s.on_cull_list = true;
  if (s.tag_lost) s.needs_tag = true;
  if (s.arrival) s.arrival_kind = s.arrival.kind;
  if (s.lease) s.leased = s.lease.direction;
  if (s.weaned_on) s.weaned = true;
  return s;
}

/** True when any lifecycle event (in, out, missing, found) is recorded. */
export function hasLifecycle(events: Array<{ type: string }>): boolean {
  return events.some((e) => LIFECYCLE.has(e.type));
}

// ---------------------------------------------------------------- presence on a date

export type Presence = "yes" | "no" | "assumed" | "unknown";

/**
 * Was this animal on hand (owned or in our care, here or missing) at the end of `on`? Uses dated
 * herd events when they exist; otherwise the record's status and birth date. "assumed" means it is
 * counted but its arrival was never dated; "unknown" means it left at a date nobody recorded.
 */
export function presenceAt(a: { timeline?: LifePoint[]; status: string; birth_date: string | null; origin: string | null }, on: string, today: string): Presence {
  if (a.status === "reference") return "no";
  if (a.birth_date && a.birth_date.length >= 10 && a.birth_date.slice(0, 10) > on) return "no";
  const points = (a.timeline ?? []).filter((p) => p.change === "in" || p.change === "out");
  if (points.length) {
    const dated = points.filter((p) => p.on);
    const undated = points.filter((p) => !p.on);
    let state: boolean | null = null;
    for (const p of dated) {
      if (p.on! > on) {
        if (state === null) state = p.change === "out";  // before the first event: present only if it was leaving
        break;
      }
      state = p.change === "in";
    }
    if (state === null) state = undated[0]?.change === "out";
    if (undated.length) {
      // An undated arrival or departure can't be placed before a past date. Only "not here yet" is certain.
      if (on < today) return !state && dated.length > 0 && dated[0].on! > on ? "no" : "unknown";
      state = undated[undated.length - 1].change === "in";
    }
    if (!state) return "no";
    const firstIn = dated.find((p) => p.change === "in");
    if (firstIn && firstIn.on! <= on) return "yes";
    return a.origin === "born" && a.birth_date ? "yes" : on >= today ? "yes" : "assumed";
  }
  const current = a.status === "active" || a.status === "missing";
  if (!current) return on >= today ? "no" : "unknown";
  if (on >= today) return "yes";
  return a.origin === "born" && a.birth_date ? "yes" : "assumed";
}

// ---------------------------------------------------------------- lots and places

export interface CountRecord { on: string | null; counted: number; expected?: number; class?: string; event_id: string }

export interface LotFacet {
  last_count?: CountRecord;
  counts: CountRecord[];
  arrived: number;
  departed: number;
  died: number;
  first_in?: string | null;
  class?: string;
  interests?: Partial<Record<InterestKind, Interest>>;
  owner_ids?: string[];
  our_share?: number;
}

export function reduceLot(events: FacetEvent[]): LotFacet | null {
  if (!events.length) return null;
  const s: LotFacet = { counts: [], arrived: 0, departed: 0, died: 0 };
  for (const e of [...events].sort(byEffective)) {
    const on = effectiveOn(e);
    if (e.type === "herd.count" && e.roles.includes("group")) {
      const d = e.data as CountData;
      const rec: CountRecord = { on, counted: d.counted, ...(d.expected !== undefined ? { expected: d.expected } : {}), ...(d.class ? { class: d.class } : {}), event_id: e.id };
      s.counts.push(rec);
      s.last_count = rec;
    }
    if (!e.roles.includes("lot")) continue;
    if (e.type === "herd.arrival") {
      s.arrived += headOf(e);
      if (s.first_in === undefined) s.first_in = on;
      const cls = (e.data as ArrivalData).class;
      if (cls && !s.class) s.class = cls;
    }
    if (e.type === "herd.departure") s.departed += headOf(e);
    if (e.type === "herd.death") s.died += headOf(e);
    if (e.type === "herd.ownership") {
      const d = e.data as OwnershipData;
      const kind = d.interest ?? "title";
      s.interests = { ...(s.interests ?? {}), [kind]: { holders: d.holders ?? [], ...(d.our_share !== undefined ? { our_share: d.our_share } : {}), since: on, ...(d.until ? { until: d.until } : {}), ...(d.basis ? { basis: d.basis } : {}), via: "ownership", event_id: e.id } };
    }
  }
  s.counts = s.counts.slice(-12);
  const title = s.interests?.title;
  if (title) {
    if (title.holders.length) s.owner_ids = title.holders.map((h) => h.contact_id);
    if (title.our_share !== undefined) s.our_share = title.our_share;
  }
  return s;
}

export interface PlaceFacet { last_count?: CountRecord; counts: CountRecord[] }

export function reducePlace(events: FacetEvent[]): PlaceFacet | null {
  const counts = [...events].sort(byEffective).filter((e) => e.type === "herd.count").map((e) => {
    const d = e.data as CountData;
    return { on: effectiveOn(e), counted: d.counted, ...(d.expected !== undefined ? { expected: d.expected } : {}), ...(d.class ? { class: d.class } : {}), event_id: e.id } as CountRecord;
  });
  if (!counts.length) return null;
  return { last_count: counts[counts.length - 1], counts: counts.slice(-12) };
}

/** Counted minus expected: negative is short, positive is over, undefined when nothing was expected. */
export function discrepancy(c: CountRecord | undefined): number | undefined {
  if (!c || c.expected === undefined) return undefined;
  return c.counted - c.expected;
}

/**
 * Head in a lot by its records on a date: the last count sets the number, then arrivals, sorting
 * in and out, sales and deaths move it. Exits of tagged members that didn't name the lot still
 * take a head out.
 */
export interface HeadMove { on: string | null; recorded_at: string; delta?: number; set?: number }

export function headAt(moves: HeadMove[], on: string): { head: number; known: boolean } {
  const sorted = moves.filter((m) => m.on && m.on <= on).sort((a, b) => (a.on === b.on ? a.recorded_at.localeCompare(b.recorded_at) : a.on!.localeCompare(b.on!)));
  let head = 0;
  for (const m of sorted) head = m.set !== undefined ? m.set : head + (m.delta ?? 0);
  return { head: Math.max(0, head), known: sorted.length > 0 };
}

/** Share totals for one right, for the "adds up to" check. */
export function shareTotal(i: { holders?: Holder[]; our_share?: number } | undefined): number | null {
  if (!i) return null;
  const parts = [...(i.holders ?? []).map((h) => h.share), i.our_share];
  if (parts.every((p) => p === undefined)) return null;
  return parts.reduce<number>((n, p) => n + (p ?? 0), 0);
}
